import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
  Container,
  TextField,
  InputAdornment,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  OpenInNew as OpenInNewIcon,
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
  Movie as MovieIcon,
  Image as ImageIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getAdminAds, deleteAd } from "../api/adsApi";
import { ROLES, hasRole } from "../utils/auth";
import { getApiErrorMessage } from "../utils/apiError";
import { AVAILABLE_PAGES, AVAILABLE_SLOTS } from "./CreateAd";

const PAGE_LIMIT = 10;

// Helper component for single-line tag rendering with rich hover popup
const TagsHoverCell = ({ items, getItemLabel, color = "default", title = "All Items" }) => {
  if (!items || items.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        None
      </Typography>
    );
  }

  const firstLabel = getItemLabel(items[0]);
  const isSecondary = color === "secondary";

  if (items.length === 1) {
    return (
      <Tooltip title={firstLabel} placement="top" arrow enterDelay={200}>
        <Box sx={{ display: "inline-block", maxWidth: "100%", verticalAlign: "middle" }}>
          <Chip
            label={firstLabel}
            size="small"
            color={color}
            variant={isSecondary ? "outlined" : "filled"}
            sx={{
              fontSize: "11px",
              maxWidth: 170,
              "& .MuiChip-label": {
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                display: "block",
              },
            }}
          />
        </Box>
      </Tooltip>
    );
  }

  return (
    <Tooltip
      arrow
      placement="top"
      enterDelay={150}
      leaveDelay={200}
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: "#ffffff",
            color: "#0f172a",
            boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.25), 0 8px 10px -6px rgba(0, 0, 0, 0.15)",
            border: "1px solid #e2e8f0",
            p: 1.5,
            borderRadius: 2.5,
            maxWidth: 340,
          },
        },
        arrow: {
          sx: {
            color: "#ffffff",
            "&::before": {
              border: "1px solid #e2e8f0",
            },
          },
        },
      }}
      title={
        <Box sx={{ p: 0.5 }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={1}
            pb={0.75}
            borderBottom="1px solid #f1f5f9"
          >
            <Typography variant="caption" fontWeight={700} color="text.primary" sx={{ letterSpacing: 0.3 }}>
              {title}
            </Typography>
            <Chip
              label={`${items.length} total`}
              size="small"
              sx={{ height: 18, fontSize: "10px", fontWeight: 700, bgcolor: "#f1f5f9", color: "#475569" }}
            />
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {items.map((item, idx) => (
              <Chip
                key={idx}
                label={getItemLabel(item)}
                size="small"
                color={color}
                variant={isSecondary ? "outlined" : "filled"}
                sx={{
                  fontSize: "11px",
                  fontWeight: 600,
                }}
              />
            ))}
          </Box>
        </Box>
      }
    >
      <Box
        display="inline-flex"
        alignItems="center"
        gap={0.5}
        sx={{
          whiteSpace: "nowrap",
          cursor: "pointer",
          maxWidth: "100%",
          verticalAlign: "middle",
        }}
      >
        <Chip
          label={firstLabel}
          size="small"
          color={color}
          variant={isSecondary ? "outlined" : "filled"}
          sx={{
            fontSize: "11px",
            maxWidth: 130,
            "& .MuiChip-label": {
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              display: "block",
            },
          }}
        />
        <Chip
          label={`+${items.length - 1} more`}
          size="small"
          color={isSecondary ? "secondary" : "primary"}
          sx={{
            fontSize: "10.5px",
            fontWeight: 700,
            height: 24,
            cursor: "pointer",
            bgcolor: isSecondary ? "#f3e8ff" : "#e0f2fe",
            color: isSecondary ? "#7c3aed" : "#0284c7",
            "&:hover": {
              bgcolor: isSecondary ? "#e9d5ff" : "#bae6fd",
            },
          }}
        />
      </Box>
    </Tooltip>
  );
};

const ManageAds = () => {
  const navigate = useNavigate();

  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");

  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [adToDelete, setAdToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Preview modal state
  const [previewAd, setPreviewAd] = useState(null);

  useEffect(() => {
    if (!hasRole(ROLES.SUPERADMIN)) {
      navigate("/unauthorized", { replace: true });
    }
  }, [navigate]);

  // 🔹 Fetch Ads
  const fetchAds = async (pageNo = page) => {
    try {
      setLoading(true);
      const res = await getAdminAds(pageNo, PAGE_LIMIT);

      if (res?.success || Array.isArray(res) || res?.data) {
        // Response format:
        // { success: true, data: { slots: [...], total: 8, page: 1, limit: 10, ads: [...] } }
        const payload = res?.data || res;
        const list = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.ads)
          ? payload.ads
          : Array.isArray(payload?.data)
          ? payload.data
          : [];

        const limit = payload?.limit || PAGE_LIMIT;
        const total = typeof payload?.total === "number" ? payload.total : list.length;
        const calculatedTotalPages =
          payload?.totalPages || (total > 0 ? Math.ceil(total / limit) : 1);

        setAds(Array.isArray(list) ? list : []);
        setTotalPages(calculatedTotalPages);
        setPage(payload?.page || payload?.currentPage || pageNo);
      }
    } catch (err) {
      console.error("Failed to load ads:", err);
      toast.error(getApiErrorMessage(err) || "Failed to load advertisements");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAds(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== page) {
      setPage(newPage);
    }
  };

  const openDeleteModal = (ad) => {
    setAdToDelete(ad);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setAdToDelete(null);
    setDeleteModalOpen(false);
  };

  const confirmDelete = async () => {
    if (!adToDelete) return;
    try {
      setDeleting(true);
      await deleteAd(adToDelete.id);
      toast.success("Ad deleted successfully");
      closeDeleteModal();
      if (ads.length === 1 && page > 1) {
        setPage((prev) => prev - 1);
      } else {
        fetchAds(page);
      }
    } catch (err) {
      console.error("Delete ad error:", err);
      toast.error(getApiErrorMessage(err) || "Failed to delete ad");
    } finally {
      setDeleting(false);
    }
  };

  // Helper to parse pages and slots
  const parseArrayField = (field) => {
    if (!field) return [];
    if (Array.isArray(field)) return field;
    try {
      const parsed = JSON.parse(field);
      return Array.isArray(parsed) ? parsed : [field];
    } catch {
      return [field];
    }
  };

  const getPageLabel = (val) => {
    const found = AVAILABLE_PAGES.find((p) => p.value === val);
    return found ? found.label : val;
  };

  const legacySlotLabels = {
    global_top_banner: "Top Banner (Header)",
    tournament_details_banner: "Top Banner (Header)",
    global_footer_logos: "Footer Banner",
    footer_logos: "Footer Banner",
    join_video: "Tournament Join Video",
    tournament_list_video: "Tournament Join Video",
    sidebar_left: "Left Side Banner",
    sidebar_right: "Right Side Banner",
  };

  const getSlotLabel = (val) => {
    const found = AVAILABLE_SLOTS.find((s) => s.value === val);
    if (found) return found.label;
    if (legacySlotLabels[val]) return legacySlotLabels[val];
    return val;
  };

  // Filter ads by search query
  const filteredAds = ads.filter((ad) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    const titleMatch = ad.title?.toLowerCase().includes(q);
    const linkMatch = ad.linkUrl?.toLowerCase().includes(q);
    return titleMatch || linkMatch;
  });

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Top Header */}
      <Box display="grid" gridTemplateColumns="1fr auto 1fr" alignItems="center" mb={3} gap={2}>
        <Box>
          <Button
            variant="contained"
            onClick={() => navigate("/Dashboard")}
            sx={{
              background: "linear-gradient(90deg, #7b1fa2 0%, #9c27b0 100%)",
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              py: 1,
              borderRadius: "4px",
              boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
              "&:hover": {
                background: "linear-gradient(90deg, #6a1b9a 0%, #8e24aa 100%)",
              },
            }}
          >
            Back to Dashboard
          </Button>
        </Box>

        <Typography variant="h4" fontWeight={700} textAlign="center">
          Advertisements Management
        </Typography>

        <Box textAlign="right">
          <Button
            variant="contained"
            onClick={() => navigate("/create-ad")}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              bgcolor: "#0041A8",
              "&:hover": { bgcolor: "#003282" },
              fontWeight: 600,
            }}
          >
            Create New Ad
          </Button>
        </Box>
      </Box>

      {/* Filter / Search Bar */}
      {/* <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 3, border: "1px solid #e2e8f0" }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
          <TextField
            size="small"
            placeholder="Search by title or link URL..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ width: { xs: "100%", sm: 380 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          <Typography variant="body2" color="text.secondary">
            Showing {filteredAds.length} {filteredAds.length === 1 ? "ad" : "ads"}
          </Typography>
        </Box>
      </Paper> */}

      {/* Ads Table */}
      <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, border: "1px solid #e2e8f0" }}>
        <Table sx={{ minWidth: 800, marginTop: 0 }}>
          <TableHead sx={{ bgcolor: "#f8fafc" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, width: 100 }}>Media</TableCell>
              <TableCell sx={{ fontWeight: 700, width: 180 }}>Title</TableCell>
              <TableCell sx={{ fontWeight: 700, width: 110 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 700, width: 200 }}>Target Pages</TableCell>
              <TableCell sx={{ fontWeight: 700, width: 220 }}>Slots</TableCell>
              <TableCell sx={{ fontWeight: 700, width: 180 }}>Destination Link</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, width: 120 }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={40} />
                </TableCell>
              </TableRow>
            ) : filteredAds.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <Typography variant="body1" color="text.secondary" gutterBottom>
                    No advertisements found.
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => navigate("/create-ad")}
                    sx={{ mt: 1, bgcolor: "#0041A8" }}
                  >
                    Create Your First Ad
                  </Button>
                </TableCell>
              </TableRow>
            ) : (
              filteredAds.map((ad) => {
                const mediaUrl = ad.url || ad.mediaUrl || ad.media || "";
                const isVideo =
                  ad.mediaType === "video" ||
                  (mediaUrl && (mediaUrl.includes(".mp4") || mediaUrl.includes(".webm")));
                const pages = parseArrayField(ad.pages);
                const slots = parseArrayField(ad.slots);

                return (
                  <TableRow key={ad.id} hover sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                    {/* Media Thumbnail */}
                    <TableCell sx={{ width: 100 }}>
                      <Box
                        sx={{
                          width: 80,
                          height: 50,
                          borderRadius: 1.5,
                          overflow: "hidden",
                          bgcolor: "#0f172a",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                        }}
                        onClick={() => setPreviewAd(ad)}
                      >
                        {mediaUrl ? (
                          isVideo ? (
                            <Box display="flex" alignItems="center" justifyContent="center">
                              <MovieIcon sx={{ color: "#38bdf8" }} />
                            </Box>
                          ) : (
                            <img
                              src={mediaUrl}
                              alt={ad.altText || ad.title}
                              style={{ width: "100%", height: "100%", objectFit: "cover" }}
                            />
                          )
                        ) : (
                          <ImageIcon sx={{ color: "grey.500" }} />
                        )}
                      </Box>
                    </TableCell>

                    {/* Title */}
                    <TableCell sx={{ width: 180, maxWidth: 180 }}>
                      <Tooltip title={ad.title} placement="top" arrow enterDelay={250}>
                        <Typography
                          variant="subtitle2"
                          fontWeight={700}
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            display: "block",
                          }}
                        >
                          {ad.title}
                        </Typography>
                      </Tooltip>
                      {ad.altText && (
                        <Tooltip title={`Alt: ${ad.altText}`} placement="top" arrow enterDelay={250}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              display: "block",
                            }}
                          >
                            Alt: {ad.altText}
                          </Typography>
                        </Tooltip>
                      )}
                    </TableCell>

                    {/* Media Type */}
                    <TableCell sx={{ width: 110 }}>
                      <Chip
                        icon={isVideo ? <MovieIcon fontSize="small" /> : <ImageIcon fontSize="small" />}
                        label={ad.mediaType ? ad.mediaType.toUpperCase() : "IMAGE"}
                        size="small"
                        color={isVideo ? "secondary" : "primary"}
                        variant="outlined"
                      />
                    </TableCell>

                    {/* Target Pages */}
                    <TableCell sx={{ width: 200, maxWidth: 200 }}>
                      <TagsHoverCell
                        items={pages}
                        getItemLabel={getPageLabel}
                        color="primary"
                        title="Target Pages"
                      />
                    </TableCell>

                    {/* Target Slots */}
                    <TableCell sx={{ width: 220, maxWidth: 220 }}>
                      <TagsHoverCell
                        items={slots}
                        getItemLabel={getSlotLabel}
                        color="secondary"
                        title="Target Ad Slots"
                      />
                    </TableCell>

                    {/* Link URL */}
                    <TableCell sx={{ width: 180, maxWidth: 180 }}>
                      {ad.linkUrl ? (
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Tooltip title={ad.linkUrl} placement="top" arrow enterDelay={250}>
                            <Typography
                              variant="body2"
                              sx={{
                                maxWidth: 130,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              <a
                                href={ad.linkUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: "#0284c7", textDecoration: "none" }}
                              >
                                {ad.linkUrl}
                              </a>
                            </Typography>
                          </Tooltip>
                          <IconButton
                            size="small"
                            component="a"
                            href={ad.linkUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <OpenInNewIcon fontSize="inherit" />
                          </IconButton>
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          No link
                        </Typography>
                      )}
                    </TableCell>

                    {/* Actions */}
                    <TableCell align="center" sx={{ width: 120 }}>
                      <Box display="flex" justifyContent="center" gap={0.5}>
                        {/* <Tooltip title="Preview Ad">
                          <IconButton size="small" color="info" onClick={() => setPreviewAd(ad)}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip> */}
                        <Tooltip title="Edit Ad">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => navigate(`/edit-ad/${ad.id}`)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Ad">
                          <IconButton size="small" color="error" onClick={() => openDeleteModal(ad)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 🔹 Pagination */}
      {ads.length > 0 && (
        <div style={{ marginTop: "20px" }}>
          <button
            onClick={() => handlePageChange(page - 1)}
            disabled={loading || page === 1}
          >
            Previous
          </button>

          <span> Page {page} of {totalPages} </span>

          <button
            onClick={() => handlePageChange(page + 1)}
            disabled={loading || page === totalPages}
          >
            Next
          </button>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteModalOpen} onClose={closeDeleteModal}>
        <DialogTitle fontWeight={700}>Confirm Ad Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the advertisement <b>"{adToDelete?.title}"</b>? This action
            cannot be undone and the ad will no longer be shown in Chesswyze web app.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeDeleteModal} disabled={deleting} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting && <CircularProgress size={18} color="inherit" />}
          >
            {deleting ? "Deleting..." : "Delete Ad"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={Boolean(previewAd)} onClose={() => setPreviewAd(null)} maxWidth="md" fullWidth>
        <DialogTitle fontWeight={700} display="flex" justifyContent="space-between" alignItems="center">
          <span>Ad Preview: {previewAd?.title}</span>
          <Chip label={previewAd?.mediaType?.toUpperCase() || "IMAGE"} color="primary" size="small" />
        </DialogTitle>
        <DialogContent dividers>
          {previewAd && (
            <Box>
              <Box
                sx={{
                  width: "100%",
                  maxHeight: 450,
                  bgcolor: "#0f172a",
                  borderRadius: 2,
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 2,
                }}
              >
                {previewAd.mediaType === "video" ||
                  (previewAd.url &&
                    (previewAd.url.includes(".mp4") || previewAd.url.includes(".webm"))) ? (
                  <video
                    src={previewAd.url || previewAd.mediaUrl || previewAd.media}
                    controls
                    autoPlay
                    style={{ maxWidth: "100%", maxHeight: 420 }}
                  />
                ) : (
                  <img
                    src={previewAd.url || previewAd.mediaUrl || previewAd.media}
                    alt={previewAd.altText || previewAd.title}
                    style={{ maxWidth: "100%", maxHeight: 420, objectFit: "contain" }}
                  />
                )}
              </Box>

              <Box display="grid" gridTemplateColumns="120px 1fr" gap={1} mb={1}>
                <Typography variant="body2" fontWeight={700}>
                  Destination:
                </Typography>
                <Typography variant="body2">
                  {previewAd.linkUrl ? (
                    <a
                      href={previewAd.linkUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "#0284c7" }}
                    >
                      {previewAd.linkUrl}
                    </a>
                  ) : (
                    "None"
                  )}
                </Typography>

                <Typography variant="body2" fontWeight={700}>
                  Target Pages:
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={0.5}>
                  {parseArrayField(previewAd.pages).map((p, i) => (
                    <Chip key={i} label={getPageLabel(p)} size="small" />
                  ))}
                </Box>

                <Typography variant="body2" fontWeight={700}>
                  Slots:
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={0.5}>
                  {parseArrayField(previewAd.slots).map((s, i) => (
                    <Chip key={i} label={getSlotLabel(s)} size="small" color="secondary" />
                  ))}
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setPreviewAd(null)} variant="outlined">
            Close
          </Button>
          {previewAd && (
            <Button
              variant="contained"
              onClick={() => {
                const id = previewAd.id;
                setPreviewAd(null);
                navigate(`/edit-ad/${id}`);
              }}
              sx={{ bgcolor: "#0041A8" }}
            >
              Edit Ad
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ManageAds;
